import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormGroup from '@material-ui/core/FormGroup';
import moment from 'moment';
import Paper from '@material-ui/core/Paper';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import Button from 'Components/UI/Button';
import NotificationList from 'Components/UI/NotificationList';
import AppContext from 'Context/AppContext';
import DatePicker from 'Components/Ant/DatePicker';
import Icon from 'Components/UI/Icon';
import RoundButton from 'Components/UI/RoundButton';
import MetaPickList from './MetaPickList';
import getMetaResourceTemplate from '../../../../Hooks/getMetaResource';
import StatusCheckbox from './StatusCheckBox';

const DEFAULT_LIMIT = 20;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  headerWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(3.25),
  },
  markAllAsReadButton: {},
  filtersIconContainer: {
    padding: theme.spacing(2),
    display: 'flex',
    marginLeft: 'auto',
    verticalAlign: 'middle',
    width: 240, // TODO: is this time for xs?
    color: theme.palette.primary.main,
    alignItems: 'center',
  },
  filtersIcon: {
    display: 'flex',
    height: 40,
    width: 40,
    borderRadius: '50%',
    backgroundColor: theme.palette.facet.lightGray,
    alignItems: 'center',
    justifyContent: 'center',
    '& *': {
      padding: theme.spacing(1),
    },
  },
  filters: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    width: 240, // TODO: is this time for xs?
    maxWidth: 240,
    padding: theme.spacing(1),
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: '1',
    maxWidth: theme.dimensions.xl,
  },
  select: {
    width: 200,
    marginBottom: theme.spacing(1),
  },
  fullName: {
    marginBottom: theme.spacing(1),
  },
  loadMoreButton: {},
}));

function NotificationCenter() {
  const classes = useStyles();
  const {
    fetchNotifications,
    markNotificationRead,
    markNotificationsRead,
  } = useContext(AppContext);
  const notificationTypes = useRef(null);
  const { rootNodeProps } = useEventTrackingLogEvent({
    nodeName: 'Notification Center',
    isPageView: true,
  });

  const [
    { totalCount, notifications, lastImportUntilMinLength },
    setNotifications,
  ] = useState({
    unreadNotificationCount: 0,
    notifications: [],
    totalCount: 0,
    lastImportUntilMinLength: 0,
  });

  const [filters, setFilters] = useState({
    primaryClientFullName: '',
    type: [],
    status: '',
  });

  const hasMore = notifications.length !== 0
    && Boolean(notifications[notifications.length - 1].meta.links.next);

  function updateNotifications(newNotifications, newQuery) {
    if (newNotifications === undefined) {
      return;
    }

    let additionalNotifications = Object.values(newNotifications.notifications);

    let updatedLastImportUntilMinLength = lastImportUntilMinLength + additionalNotifications.length;

    if (updatedLastImportUntilMinLength >= DEFAULT_LIMIT) {
      updatedLastImportUntilMinLength = 0;
    }

    if (!newQuery) {
      additionalNotifications = [...notifications, ...additionalNotifications];
    }

    setNotifications({
      unreadNotificationCount: newNotifications.unreadNotificationCount,
      notifications: additionalNotifications,
      totalCount: totalCount + newNotifications.totalCount,
      lastImportUntilMinLength: updatedLastImportUntilMinLength,
    });
  }

  function getNotifications(queries = {}, uri = null, newQuery = false) {
    let notificationPromise = null;

    if (uri) {
      notificationPromise = fetchNotifications({}, uri);
    } else {
      notificationPromise = fetchNotifications({
        ...queries,
        createdAtMin: queries.createdAtMin
          ? moment(queries.createdAtMin)
            .startOf('day')
            .toJSON()
          : undefined,
        createdAtMax: queries.createdAtMin
          ? moment(queries.createdAtMax)
            .endOf('day')
            .toJSON()
          : undefined,
        limit: DEFAULT_LIMIT,
        offset: 0,
      });
    }

    notificationPromise.then((groups) => {
      if (groups.meta !== null) {
        notificationTypes.current = getMetaResourceTemplate(
          groups.meta.template,
          'type',
          def => def,
        );
      }

      updateNotifications(groups, newQuery);
    });
  }

  const updateFiltersAndQuery = (queries = {}) => {
    setFilters(queries);
    getNotifications(queries, null, true);
  };

  const markAllNotificationsRead = () => markNotificationsRead({
    ...filters,
    offset: 0,
    limit: totalCount,
  }).then((groups) => {
    updateNotifications(groups, true);
  });
  const fetchNext = () => {
    const nextLink = notifications[notifications.length - 1].meta.links.next;
    const queries = {};
    queries.limit = DEFAULT_LIMIT;
    queries.offset = Number(queries.offset) + 1;

    fetchNotifications({}, nextLink).then((groups) => {
      if (groups.meta !== null) {
        notificationTypes.current = getMetaResourceTemplate(
          groups.meta.template,
          'type',
          def => def,
        );
      }

      updateNotifications(groups);
    });
  };

  const handleFilterChange = (event) => {
    const {
      target: { name, value },
    } = event;
    const queries = {
      ...filters,
      [name]: value,
    };

    updateFiltersAndQuery(queries);
  };

  const handleTypeSelect = value => updateFiltersAndQuery({
    ...filters,
    type: value.includes(null) ? [] : value,
  });

  const markAsReadAndFetch = (id) => {
    markNotificationRead(id).then(() => getNotifications());
  };

  useEffect(() => {
    getNotifications({});
  }, []);

  useEffect(() => {
    if (
      lastImportUntilMinLength > 0
      && lastImportUntilMinLength < DEFAULT_LIMIT
      && notifications.length > 0
      && notifications[notifications.length - 1].meta.links.next
    ) {
      getNotifications(
        {},
        notifications[notifications.length - 1].meta.links.next,
      );
    }
  }, [notifications]);

  return (
    <div
      className={classes.root}
      {...rootNodeProps}
    >
      <div className={classes.filtersContainer}>
        <div className={classes.filtersIconContainer}>
          <span className={classes.filtersIcon}>
            <Icon icon="notificationFilters" height="24px" width="24px" />
          </span>
          <Typography variant="h6" component="div" color="primary">
            &nbsp;Filters
          </Typography>
        </div>
        <FormGroup className={classes.filters}>
          <TextField
            name="primaryClientFullName"
            label="Primary client full name"
            className={classes.fullName}
            value={filters.primaryClientFullName}
            onChange={handleFilterChange}
          />
          <Typography color="primary">Status</Typography>
          <StatusCheckbox
            label="All"
            onChange={handleFilterChange}
            value=""
            currentValue={filters.status}
          />
          <StatusCheckbox
            label="Read"
            onChange={handleFilterChange}
            value="read"
            currentValue={filters.status}
          />
          <StatusCheckbox
            label="Unread"
            onChange={handleFilterChange}
            value="unread"
            currentValue={filters.status}
          />
          {notificationTypes.current && (
            <>
              <Typography color="primary">Type</Typography>
              <MetaPickList
                handleSelect={handleTypeSelect}
                {...notificationTypes.current}
                value={filters.type}
                className={classes.select}
              />
            </>
          )}
          <Typography color="primary">Date</Typography>
          <DatePicker.RangePicker
            className={classes.select}
            name="createdAt"
            format="MM/DD/YYYY"
            separator=" - "
            value={[filters.createdAtMin, filters.createdAtMax]}
            onChange={([createdAtMin, createdAtMax]) => updateFiltersAndQuery({
              ...filters,
              createdAtMin,
              createdAtMax,
            })
            }
          />
        </FormGroup>
      </div>
      <div className={classes.main}>
        <div className={classes.headerWrapper}>
          <RoundButton
            size="medium"
            color="primary"
            disableElevation
            variant="contained"
            onClick={markAllNotificationsRead}
            className={classes.markAllAsReadButton}
          >
            Mark these as read
          </RoundButton>
        </div>
        <Paper elevation={5}>
          <NotificationList
            notifications={notifications}
            updateNotification={markAsReadAndFetch}
            expand
            footer={
              hasMore && (
                <Button
                  onClick={fetchNext}
                  className={classes.loadMoreButton}
                  disableElevation
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  Load more
                </Button>
              )
            }
          />
        </Paper>
      </div>
    </div>
  );
}

export default NotificationCenter;
